/**
 * Carga y ejecucion de reCAPTCHA Enterprise.
 *
 * Vivia dentro de ContactWizard. Se extrae porque ahora hay dos formularios que
 * necesitan token -el wizard y la captura rapida de email- y el backend rechaza
 * cualquier envio sin el: recaptcha_token es obligatorio en el Validator.
 *
 * La promesa del script es a nivel modulo a proposito: si los dos formularios
 * estan en la misma pagina, el script de Google se pide una sola vez.
 */

export const RECAPTCHA_ACTION = "contact_form_submit";

/** Accion del login del panel: el backend la exige (api/public/panel/login.php),
 *  asi un token del formulario de contacto no sirve para entrar al panel. */
export const PANEL_LOGIN_ACTION = "panel_login";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string },
        ) => Promise<string>;
      };
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(siteKey: string, onError: string): Promise<void> {
  if (window.grecaptcha?.enterprise) return Promise.resolve();

  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-recaptcha-enterprise="true"]',
      );

      if (existing) {
        if (window.grecaptcha?.enterprise) {
          resolve();
          return;
        }

        if (existing.dataset.recaptchaEnterpriseFailed === "true") {
          existing.remove();
        } else {
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener(
            "error",
            () => {
              existing.dataset.recaptchaEnterpriseFailed = "true";
              existing.remove();
              // Se limpia la promesa para que un reintento vuelva a pedir el
              // script en vez de quedar cacheado el fallo para siempre.
              scriptPromise = null;
              reject(new Error(onError));
            },
            { once: true },
          );
          return;
        }
      }

      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.dataset.recaptchaEnterprise = "true";
      script.onload = () => resolve();
      script.onerror = () => {
        script.dataset.recaptchaEnterpriseFailed = "true";
        script.remove();
        scriptPromise = null;
        reject(new Error(onError));
      };
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

interface TokenErrors {
  load: string;
  unavailable: string;
}

/**
 * Devuelve un token listo para mandar al backend. Lanza si no se puede.
 * `action` es la del formulario de contacto salvo que se pida otra (el login
 * del panel usa PANEL_LOGIN_ACTION): el backend rechaza un token de otra accion.
 */
export async function getRecaptchaToken(
  siteKey: string,
  errors: TokenErrors,
  action: string = RECAPTCHA_ACTION,
): Promise<string> {
  await loadScript(siteKey, errors.load);

  return new Promise<string>((resolve, reject) => {
    const grecaptcha = window.grecaptcha?.enterprise;

    if (!grecaptcha) {
      reject(new Error(errors.unavailable));
      return;
    }

    grecaptcha.ready(() => {
      grecaptcha
        .execute(siteKey, { action })
        .then(resolve)
        .catch(reject);
    });
  });
}
